/**
 * Thin FE wrappers for `/api/v1/customers/*`.
 *
 * Note on list-response shape:
 *   The shared `CustomerListResponse` schema models `{items, total}`, which
 *   is what the API _documentation_ advertises. The API implementation
 *   (`apps/api/src/routes/customers.ts`) actually returns `{items, total,
 *   page, pageSize}` so the table UI can echo back its current page without
 *   threading the query through separately. We extend the shared schema here
 *   to match reality, without weakening the existing contract elsewhere.
 *   If/when the shared schema is updated to match, this local extension
 *   collapses to a direct `parse`.
 */
import { customers } from '@dragonis-cdr/shared';
import { z } from 'zod';

import { apiFetch } from './client';

export type CustomerRecord = customers.CustomerRecord;
export type CustomerCreateRequest = customers.CustomerCreateRequest;
export type CustomerUpdateRequest = customers.CustomerUpdateRequest;

/**
 * Extended list envelope — see module-level note.
 */
const CustomerListEnvelope = customers.CustomerListResponse.extend({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type CustomerListResponse = z.infer<typeof CustomerListEnvelope>;

export interface ListCustomersParams {
  page?: number;
  pageSize?: number;
}

function buildQuery(params: Record<string, number | undefined>): string {
  const entries = Object.entries(params).filter((entry): entry is [string, number] => {
    const value = entry[1];
    return typeof value === 'number' && Number.isFinite(value);
  });
  if (entries.length === 0) return '';
  const usp = new URLSearchParams();
  for (const [key, value] of entries) {
    usp.set(key, String(value));
  }
  return `?${usp.toString()}`;
}

export async function listCustomers(
  params: ListCustomersParams = {},
): Promise<CustomerListResponse> {
  const query = buildQuery({
    page: params.page,
    pageSize: params.pageSize,
  });
  const body = await apiFetch<unknown>(`/customers${query}`);
  return CustomerListEnvelope.parse(body);
}

export async function getCustomer(id: string): Promise<CustomerRecord> {
  const body = await apiFetch<unknown>(`/customers/${encodeURIComponent(id)}`);
  return customers.CustomerRecord.parse(body);
}

export async function createCustomer(input: CustomerCreateRequest): Promise<CustomerRecord> {
  const body = await apiFetch<unknown>('/customers', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return customers.CustomerRecord.parse(body);
}

export async function updateCustomer(
  id: string,
  input: CustomerUpdateRequest,
): Promise<CustomerRecord> {
  const body = await apiFetch<unknown>(`/customers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return customers.CustomerRecord.parse(body);
}

/**
 * DELETE is a soft delete server-side — the row is retained with
 * `deletedAt` set. The endpoint returns 204.
 */
export async function softDeleteCustomer(id: string): Promise<void> {
  await apiFetch<void>(`/customers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
